import {
  addToast,
  Button,
  DateInput,
  Select,
  SelectOptionType,
  TextAreaInput,
  TextInput,
} from '@octano/global-ui';
import {
  Fragment,
  ReactChild,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'reactstrap';
import {
  getEventsRequest,
  PeriodAcademicCalendarEvent,
} from '../../../api/requests/periods';
import { PeriodType } from '../../../api/requests/periodsType';
import { SectionTitle } from '../../../components/text';
import { useDateRange } from '../../../hooks/useDateRange';
import { useLayoutState } from '../../../hooks/useLayoutState';
import { useValidations } from '../../../hooks/useValidations';
import { capitalizeFirst } from '../../../utils/text';
import { EventForm, EventValues } from './event/EventForm';
import PeriodAcademicCalendarTable from './PeriodAcademicCalendarTable';
import PeriodCantCreateAcademicCalendarModal from './PeriodCantCreateAcademicCalendarModal';
import PeriodsByTypeLoader from './PeriodsLoader';

export interface PeriodValues {
  code: string;
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  academicYear: number;
  periodType: SelectOptionType;
  creditMin: number;
  creditMax: number;
}

export interface AcademicCalendar {
  id?: number;
  startDate?: string | null;
  endDate?: string | null;
  eventCalendar: PeriodAcademicCalendarEvent;
  previousTuitionPeriodId?: number | string;
  requirePreviousTuition?: boolean | null;
  allowOnlineTuition?: boolean | null;
  allowSectionScheduleConflicts?: boolean | null;
}

export interface PeriodFormProps {
  periodsType: PeriodType[];
  defaultAcademicCalendars?: AcademicCalendar[];
  defaultValues?: Partial<PeriodValues>;
  children: (props: { isSubmitting: boolean }) => ReactChild;
  onSubmit: (
    values: PeriodValues & { academicCalendars: any } & {
      periodTypeId: number | string;
    },
  ) => Promise<'CONNECTION' | 'HTTP_ERROR' | 'NOT_UNIQUE_CODE' | undefined>;
}

export interface EventData
  extends Omit<
    EventValues,
    'event' | 'eventStartDate' | 'eventEndDate' | 'previousTuitionPeriodId'
  > {
  eventId: number;
  startDate: string;
  endDate: string;
  hasRequiredCredits: boolean;
  hasProcessConfig: boolean;
  previousTuitionPeriodId?: number | string;
}

const PeriodsForm = (props: PeriodFormProps) => {
  const {
    periodsType = [],
    defaultAcademicCalendars = [],
    defaultValues: defaultValuesProp,
    onSubmit: onSubmitProp,
    children,
  } = props;

  const { showErrorModal } = useLayoutState();

  const { t } = useTranslation();
  const prefix = 'maintainers.periodsForm';
  const [eventsList, setEventsList] = useState<PeriodAcademicCalendarEvent[]>(
    [],
  );
  const [showEventForm, setShowEventForm] = useState<boolean>(false);
  const [
    showCantCreateEventModal,
    setShowCantCreateEventModal,
  ] = useState<boolean>(false);
  const [
    activeEventToEdit,
    setActiveEventToEdit,
  ] = useState<AcademicCalendar | null>(null);

  const [formEvents, setFormEvents] = useState<AcademicCalendar[]>(
    defaultAcademicCalendars,
  );
  const [periodTypeSelect, setPeriodTypeSelect] = useState<SelectOptionType[]>(
    [],
  );

  const defaultValues = Object.assign(
    {
      code: '',
      name: '',
      description: '',
      startDate: '',
      endDate: '',
      periodType: '',
      creditMin: 0,
      creditMax: 0,
      academicYear: '',
    },
    defaultValuesProp,
  );

  const methods = useForm<PeriodValues>({
    mode: 'onSubmit',
    defaultValues,
  });
  const {
    handleSubmit,
    formState: { isSubmitting },
    control,
    setError,
    watch,
    setValue,
  } = methods;

  const [
    startDate,
    endDate,
    periodName,
    code,
    periodCreditMin,
    periodCreditMax,
    periodType,
  ] = watch([
    'startDate',
    'endDate',
    'name',
    'code',
    'creditMin',
    'creditMax',
    'periodType',
  ]);

  const isFilledForm = startDate && endDate && periodName && code;

  const setEndDate = useCallback((e) => setValue('endDate', e), [setValue]);
  const { minDate, endDateDisabled } = useDateRange({
    startDate,
    endDate,
    setEndDate,
  });

  const {
    msgValidations,
    rulesPatternNumeric,
    validateTextNotEmpty,
    validateAlphaNumeric,
    validateMinLength,
    validateMaxLength,
  } = useValidations();

  const onSubmit = useCallback(
    async (formValues: PeriodValues) => {
      const periodTypeId = formValues.periodType?.value;
      const data = {
        ...formValues,
        academicCalendars: formEvents,
        periodTypeId: periodTypeId,
      };
      const response = await onSubmitProp(data);
      if (response === 'CONNECTION') {
        showErrorModal();
      } else if (response === 'NOT_UNIQUE_CODE') {
        setError('code', {
          message: t('common.errors.codeError', { code: formValues.code }),
        });
      } else if (response === 'HTTP_ERROR') {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t('common.errors.save'),
        });
      }
    },
    [onSubmitProp, showErrorModal, setError, formEvents, t],
  );

  const changeEventsTable = useCallback(
    (eventData: EventData, isEdition: boolean = false) => {
      if (eventData.hasRequiredCredits) {
        setValue('creditMin', eventData.creditMin);
        setValue('creditMax', eventData.creditMax);
      }

      if (isEdition) {
        setFormEvents((currentAcademicCalendars) => {
          const index = currentAcademicCalendars.findIndex(
            (a) => a.eventCalendar.id === eventData.eventId,
          );

          if (index < 0) {
            return currentAcademicCalendars;
          }

          const eventCalendar = eventsList.find(
            (e) => e.id === eventData.eventId,
          );

          if (!eventCalendar) {
            return currentAcademicCalendars;
          }
          const newAcademicCalendars = [...currentAcademicCalendars];
          newAcademicCalendars[index] = {
            id: currentAcademicCalendars[index].id,
            startDate: eventData.startDate,
            endDate: eventData.endDate,
            eventCalendar,
            requirePreviousTuition: eventData.requirePreviousTuition,
            previousTuitionPeriodId: eventData.previousTuitionPeriodId,
            allowSectionScheduleConflicts:
              eventData.allowSectionScheduleConflicts,
            allowOnlineTuition: eventData.allowOnlineTuition,
          };
          return newAcademicCalendars;
        });
      } else {
        setFormEvents((currentAcademicCalendars) => {
          const eventCalendar = eventsList.find(
            (e) => e.id === eventData.eventId,
          );

          if (!eventCalendar) {
            return currentAcademicCalendars;
          }
          return [
            ...currentAcademicCalendars,
            {
              startDate: eventData.startDate,
              endDate: eventData.endDate,
              eventCalendar,
              requirePreviousTuition: eventData.requirePreviousTuition,
              previousTuitionPeriodId: eventData.previousTuitionPeriodId,
              allowSectionScheduleConflicts:
                eventData.allowSectionScheduleConflicts,
              allowOnlineTuition: eventData.allowOnlineTuition,
            },
          ];
        });
      }
    },
    [eventsList, setValue],
  );

  const getEvents = async () => {
    const { data, error } = await getEventsRequest();
    if (error) {
      return;
    }
    setEventsList(data ?? []);
  };

  useEffect(() => {
    getEvents();
  }, []);

  useEffect(() => {
    setPeriodTypeSelect(
      periodsType.map((e) => ({
        // El catálogo guarda los nombres en minúscula ("semestre"); la
        // mayúscula inicial es sólo de presentación.
        label: capitalizeFirst(e.name),
        value: e.id,
      })),
    );
  }, [periodsType]);

  const showOnlyPeriodType = defaultValuesProp !== undefined;

  const selectedEventsIds = useMemo(() => {
    return formEvents.map((f) => f.eventCalendar.id);
  }, [formEvents]);

  return (
    <Fragment>
      <FormProvider {...methods}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Row>
            <Col xs={12} md={6}>
              <SectionTitle text={t(`${prefix}.periodData`)} />

              <Row>
                <Col xs={12} md={12} className="pb-3">
                  <Select
                    name="periodType"
                    label={t(`${prefix}.selectPeriodType`)}
                    options={periodTypeSelect}
                    control={control}
                    rules={{ required: msgValidations.required }}
                    disabled={showOnlyPeriodType}
                  />
                </Col>

                <Col xs={12} md={6}>
                  <div className="pb-3">
                    <TextInput
                      name="code"
                      label={t(`common.forms.code`)}
                      control={control}
                      rules={{
                        required: msgValidations.required,
                        maxLength: {
                          value: 50,
                          message: t('common.validations.maxLength', {
                            length: 50,
                          }),
                        },
                        validate: validateAlphaNumeric,
                      }}
                    />
                  </div>
                </Col>

                <Col xs={12} md={6}>
                  <div className="pb-3">
                    <TextInput
                      name="name"
                      label={t(`common.forms.name`)}
                      control={control}
                      rules={{
                        minLength: {
                          value: 5,
                          message: t('common.validations.minLength', {
                            length: 5,
                          }),
                        },
                        maxLength: {
                          value: 255,
                          message: t('common.validations.maxLength', {
                            length: 255,
                          }),
                        },
                        validate: validateTextNotEmpty,
                      }}
                    />
                  </div>
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={6}>
                  <div className="pb-3">
                    <DateInput
                      name="startDate"
                      label={t(`common.forms.startDate`)}
                      control={control}
                      rules={{ required: msgValidations.required }}
                    />
                  </div>
                </Col>
                <Col xs={12} md={6}>
                  <div className="pb-3">
                    <DateInput
                      name="endDate"
                      label={t(`common.forms.endDate`)}
                      minDate={minDate}
                      disabled={endDateDisabled}
                      control={control}
                      rules={{ required: msgValidations.required }}
                    />
                  </div>
                </Col>
                <Col xs={12} md={6}>
                  <TextInput
                    name="academicYear"
                    label={t('common.forms.academicYear')}
                    control={control}
                    rules={{
                      required: msgValidations.required,
                      pattern: rulesPatternNumeric,
                      validate: {
                        minLength: validateMinLength(4),
                        maxLength: validateMaxLength(4),
                      },
                    }}
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  <TextAreaInput
                    name="description"
                    label={`${t('common.forms.description')} ${t(
                      'common.forms.optionalLabel',
                    )}`}
                    control={control}
                    height={200}
                    placeholder={t(`common.forms.descriptionPlaceholder`)}
                  />
                </Col>
              </Row>
            </Col>
            <Col xs={12} md={6} style={{ borderLeft: '1px solid #F4F4F4' }}>
              <SectionTitle text={t(`${prefix}.academicCalendarDates`)} />
              <Button
                text={t(`${prefix}.addEvent`)}
                type="button"
                size="sm"
                icon="plus"
                className="g-add-button mb-3"
                onClick={() =>
                  isFilledForm
                    ? setShowEventForm(true)
                    : setShowCantCreateEventModal(true)
                }
              />
              <PeriodAcademicCalendarTable
                academicCalendars={formEvents}
                onEdit={(a) => {
                  setActiveEventToEdit(a);
                  setShowEventForm(true);
                }}
              />
            </Col>
          </Row>
          {children({ isSubmitting })}
        </Form>
      </FormProvider>
      {periodType?.value && (
        <PeriodsByTypeLoader periodTypeId={periodType.value}>
          {(periods) => (
            <EventForm
              recordToEdit={activeEventToEdit}
              changeEventsTable={changeEventsTable}
              periodName={periodName}
              eventsList={eventsList}
              periodsList={periods}
              show={showEventForm || !!activeEventToEdit}
              onClose={() => {
                setActiveEventToEdit(null);
                setShowEventForm(false);
              }}
              selectedEventsIds={selectedEventsIds}
              periodCreditMin={periodCreditMin}
              periodCreditMax={periodCreditMax}
            />
          )}
        </PeriodsByTypeLoader>
      )}
      <PeriodCantCreateAcademicCalendarModal
        open={showCantCreateEventModal}
        onClose={() => {
          setShowCantCreateEventModal(false);
        }}
      />
    </Fragment>
  );
};

export default PeriodsForm;
